import type { ApiClient } from '../client'
import type {
  ActiveFilter,
  CreateProviderRequest,
  CreateResourceRequest,
  CreateServiceRequest,
  CreateTimeOffRequest,
  Provider,
  ReplaceSchedulesRequest,
  Resource,
  ScheduleBlock,
  Service,
  TimeOff,
  UpdateProviderRequest,
  UpdateResourceRequest,
  UpdateServiceRequest,
} from '../types'

interface Retired {
  readonly id: string
  readonly active: false
}

/** src/modules/catalog/services-catalog/controllers/services-catalog.controller.ts */
export function servicesEndpoints(api: ApiClient) {
  return {
    list: (query: { active?: ActiveFilter; category?: string } = {}) =>
      api.get<readonly Service[]>('services', { query }),
    get: (id: string) => api.get<Service>(`services/${id}`),
    create: (body: CreateServiceRequest) => api.post<Service>('services', body),
    update: (id: string, body: UpdateServiceRequest) => api.patch<Service>(`services/${id}`, body),
    /** Retire, not delete: the row stays so historical appointments remain readable. */
    retire: (id: string) => api.delete<Retired>(`services/${id}`),
  }
}

/** src/modules/catalog/resources/controllers/resources.controller.ts — writes 422 in Layer Mode. */
export function resourcesEndpoints(api: ApiClient) {
  return {
    list: (query: { active?: ActiveFilter; location_id?: string } = {}) =>
      api.get<readonly Resource[]>('resources', { query }),
    get: (id: string) => api.get<Resource>(`resources/${id}`),
    create: (body: CreateResourceRequest) => api.post<Resource>('resources', body),
    update: (id: string, body: UpdateResourceRequest) =>
      api.patch<Resource>(`resources/${id}`, body),
    deactivate: (id: string) => api.delete<Retired>(`resources/${id}`),
  }
}

/** src/modules/providers/controllers/providers.controller.ts */
export function providersEndpoints(api: ApiClient) {
  return {
    list: (query: { active?: ActiveFilter; location_id?: string } = {}) =>
      api.get<readonly Provider[]>('providers', { query }),
    get: (id: string) => api.get<Provider>(`providers/${id}`),
    create: (body: CreateProviderRequest) => api.post<Provider>('providers', body),
    update: (id: string, body: UpdateProviderRequest) =>
      api.patch<Provider>(`providers/${id}`, body),
    deactivate: (id: string) => api.delete<Retired>(`providers/${id}`),
    schedules: (id: string) => api.get<readonly ScheduleBlock[]>(`providers/${id}/schedules`),
    /** Replaces the whole weekly set. 409 SCHEDULE_OVERLAP · 422 end_time must be after start_time. */
    replaceSchedules: (id: string, body: ReplaceSchedulesRequest) =>
      api.put<readonly ScheduleBlock[]>(`providers/${id}/schedules`, body),
    timeOff: (id: string) => api.get<readonly TimeOff[]>(`providers/${id}/time-off`),
    addTimeOff: (id: string, body: CreateTimeOffRequest) =>
      api.post<TimeOff>(`providers/${id}/time-off`, body),
    removeTimeOff: (id: string, timeOffId: string) =>
      api.delete<{ readonly id: string }>(`providers/${id}/time-off/${timeOffId}`),
  }
}
